/**
 * JoinPhoneStep — phone entry on the /join/<token> page.
 *
 * Self-contained RHF + Zod form (no Zustand): the parent owns the
 * stage transition via `onCodeSent`. Mirrors the SignInForm shape so
 * users see a consistent visual on both the sign-in and join surfaces.
 *
 * Why we don't reuse `SignInForm` directly: it's coupled to the
 * onboarding Zustand store + router-driven navigation. Lifting that out
 * would entangle two unrelated flows. The shared atoms (PhoneField,
 * Submit, Card) are reused — that's where consistency lives.
 */
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Card, CardBody, Form, PhoneField, Submit, SubmitProbe } from "@flint/ui";
import { identity } from "@flint/api-contract";
import type { z } from "zod";
import { trpc } from "../../lib/trpc.ts";

type FormValues = z.infer<typeof identity.requestOtpInput>;

export interface JoinPhoneStepProps {
  /** Hint text under the field — usually the masked phone the invite was sent to. */
  hint?: React.ReactNode;
  /** Bubbled to parent on success so it can advance to the OTP step. */
  onCodeSent: (args: { phone: string; challengeId: string }) => void;
}

export function JoinPhoneStep({ hint, onCodeSent }: JoinPhoneStepProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(identity.requestOtpInput),
    defaultValues: { phone: "", channel: "sms" },
  });

  const requestOtp = trpc.identity.requestOtp.useMutation();

  const onSubmit = form.handleSubmit(async (values) => {
    const result = await requestOtp.mutateAsync(values);
    onCodeSent({ phone: values.phone, challengeId: result.challengeId });
  });

  return (
    <Form {...form}>
      <SubmitProbe>
        <form onSubmit={onSubmit} noValidate>
          <Card>
            <CardBody className="flex flex-col gap-3.5">
              <PhoneField name="phone" label="Your mobile number" />
              {hint ? <p className="text-xs text-muted -mt-1">{hint}</p> : null}
              <Submit pendingLabel="Sending code" disabled={requestOtp.isPending}>
                Send code
              </Submit>
              {requestOtp.error ? (
                <Alert
                  intent="bad"
                  title="Couldn't send code"
                  description={requestOtp.error.message}
                />
              ) : null}
            </CardBody>
          </Card>
        </form>
      </SubmitProbe>
    </Form>
  );
}
